import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Button } from 'primeng/button';
import { saveAs } from 'file-saver';
import { OutingService } from '../../services/outing.service';
import { PublicOutingPhotos, PublicOutingPhoto } from '../../models/outing.model';

// Public, token-based photos page (no auth) — reached from the "View outing photos" button
// in the Marketing "Done" email. View + download each photo, or download all as a zip.
@Component({
  selector: 'app-outing-photos',
  standalone: true,
  imports: [CommonModule, Button],
  templateUrl: './outing-photos.component.html',
  styleUrl: './outing-photos.component.scss'
})
export class OutingPhotosComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private outingService = inject(OutingService);

  token = '';
  data: PublicOutingPhotos | null = null;
  loading = true;
  notFound = false;
  downloadingAll = false;

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') ?? '';
    if (!this.token) {
      this.loading = false;
      this.notFound = true;
      return;
    }

    this.outingService.getPublicPhotos(this.token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => { this.data = data; this.loading = false; },
        error: () => { this.notFound = true; this.loading = false; }
      });
  }

  download(photo: PublicOutingPhoto): void {
    this.outingService.downloadPublicPhoto(this.token, photo.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (blob) => saveAs(blob, photo.fileName), error: () => {} });
  }

  downloadAll(): void {
    if (this.downloadingAll || !this.data?.photos.length) return;
    this.downloadingAll = true;
    this.outingService.downloadAllPhotos(this.token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          const safe = (this.data?.eventName || 'outing').replace(/[^\w\-]+/g, '_');
          saveAs(blob, `${safe}_photos.zip`);
          this.downloadingAll = false;
        },
        error: () => { this.downloadingAll = false; }
      });
  }
}
